import { RemoteConfigModel } from 'src/models/remoteConfig.models'

/**
 * Check if SSO Login feature is enabled for the user.
 * Returns true if either:
 * 1. The global enable_sso_login flag is enabled, OR
 * 2. The user's organization ID is in the enable_sso_login_whitelist
 *
 * Note: On the sign-in page, organizationId may not be available yet.
 * In that case, only the global flag is checked.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional for sign-in page)
 * @returns true if SSO Login is enabled for this user
 */
export const isSSOLoginEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { enable_sso_login, enable_sso_login_whitelist } = remoteConfig

  // If org ID is available, check the whitelist
  if (organizationId) {
    return (
      enable_sso_login || enable_sso_login_whitelist.includes(organizationId)
    )
  }

  // On sign-in page (no org ID), only use the global flag
  return enable_sso_login
}

/**
 * Check if AI Insights feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_ai_insights flag is enabled, OR
 * 2. The user's organization ID is in the ai_insights_allowlist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if AI Insights is enabled for this user
 */
export const isAIInsightsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_ai_insights, ai_insights_allowlist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_ai_insights || ai_insights_allowlist.includes(organizationId)
}

/**
 * Check if Extend AI Agent feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_extend_ai_agent flag is enabled, OR
 * 2. The user's organization ID is in the extendAIWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Extend AI Agent is enabled for this user
 */
export const isExtendAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_extend_ai_agent, extendAIWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_extend_ai_agent || extendAIWhitelist.includes(organizationId)
}

/**
 * Check if the spend reporting dashboard is enabled for the user.
 * Returns true if either:
 * 1. The global enable_spend_reporting_dashboard flag is enabled, OR
 * 2. The user's organization ID is in the
 *    enable_spend_reporting_dashboard_whitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if the spend reporting dashboard is enabled for this user
 */
export const isSpendReportingDashboardEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const {
    enable_spend_reporting_dashboard,
    enable_spend_reporting_dashboard_whitelist,
  } = remoteConfig

  return (
    enable_spend_reporting_dashboard ||
    enable_spend_reporting_dashboard_whitelist.includes(organizationId)
  )
}

/**
 * Check if Suggestions and Predictions feature is enabled for the user.
 * Requires all of:
 * 1. The global show_suggestions_and_predictions kill switch is enabled, AND
 * 2. The user has an active ERP integration.
 *
 * Non-SaaS issuer exclusion is handled upstream by the backend
 * AI_EXPENSE_CATEGORIES entitlement; this util only enforces the kill switch
 * and ERP-connected requirement.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @param isErpConnected - Whether the org has an active ERP integration
 * @returns true if Suggestions and Predictions is enabled for this user
 */
export const isSuggestionsAndPredictionsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
  isErpConnected: boolean,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  return remoteConfig.show_suggestions_and_predictions && isErpConnected
}

/**
 * Check if the ERP disconnect AI-loss warning modal is enabled.
 * EX-39491 — when on, the final disconnect confirmation shows a scary
 * AI-aware warning and requires the admin to type "DISCONNECT".
 */
export const isErpDisconnectAiWarningEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_erp_disconnect_ai_warning
}
